import React, { useMemo, useState } from "react";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import FloatingTooltip from "../../Utility/FloatingTooltip/FloatingTooltip";
import TimeSheetFilter from "./TimeSheetFilter";
import { RiPencilLine, RiDeleteBinFill } from "react-icons/ri";
import { useSorting, SortableHeader } from "../../Utility/Sorting";
import { ContentLoader } from "../../Utility/Loader";
import NoDataFound from "../../Utility/NoDataFound/NoDataFound";
import DeleteConfirmModal from "../../Utility/DeleteConfirmModal/DeleteConfirmModal";
import LogTimeModal from "../Issues/LogTimeModal/LogTimeModal";
import { formatDurationWithUnit } from "./timeEntryDisplayUtils";
import TimeEntryTableTotalsBar from "./TimeEntryTableTotalsBar";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";
import PermissionDeniedModal from "../../Utility/PermissionDeniedModal/PermissionDeniedModal";
import ListPaginationBar from "../../Utility/Pagination/ListPaginationBar";

function flattenTimeEntryGroups(groups) {
  if (!Array.isArray(groups)) return [];
  return groups.flatMap((g) => (Array.isArray(g?.entries) ? g.entries : []));
}

const TimeSheet = ({
  loading,
  error,
  groups,
  filters,
  onFilterChange,
  filterOptions,
  summary,
  dateRangeLabel,
  onPrevWeek,
  onNextWeek,
  disableNext,
  applyFilters = () => {},
  dateRangeDraft = null,
  onDateRangeDraftChange,
  handleFilterReset,
  filterExpanded,
  onFilterExpandedChange,
  showTeam = false,
  showUser = false,
  showTask = true,
  showStatus = true,
  showBillingType = true,
  showLogTime = true,
  showExport = true,
  showDeleteAction = true,
  onDeleteEntry = null,
  onEditEntry = null,
  onRefetchTimeEntries = () => {},
  saving: _saving = false,
  onExportCsv = null,
  onExportXlsx = null,
  workItemPickerQueryParams,
  pagination = null,
  showNewestFirstToggle = false,
  newestFirst = false,
  onNewestFirstChange,
  preserveApiRowOrder = false,
}) => {
  const { can } = usePermissions();
  const [isLogTimeOpen, setIsLogTimeOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [entryToEdit, setEntryToEdit] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);

  const tryOpenLogTime = () => {
    if (!can(PERMISSION_KEYS.TIME_ENTRY_CREATE)) {
      setDeniedPermissionKey(PERMISSION_KEYS.TIME_ENTRY_CREATE);
      return;
    }
    setIsLogTimeOpen(true);
  };

  const tryEditEntry = (item) => {
    if (!can(PERMISSION_KEYS.TIME_ENTRY_EDIT)) {
      setDeniedPermissionKey(PERMISSION_KEYS.TIME_ENTRY_EDIT);
      return;
    }
    setEntryToEdit(item);
  };

  const tryDeleteEntry = (item) => {
    if (!can(PERMISSION_KEYS.TIME_ENTRY_DELETE)) {
      setDeniedPermissionKey(PERMISSION_KEYS.TIME_ENTRY_DELETE);
      return;
    }
    setEntryToDelete(item);
  };

  const canEditTimeEntry = can(PERMISSION_KEYS.TIME_ENTRY_EDIT);
  const canDeleteTimeEntry = showDeleteAction && can(PERMISSION_KEYS.TIME_ENTRY_DELETE);
  const showActionsColumn = canEditTimeEntry || canDeleteTimeEntry;

  const entries = useMemo(() => flattenTimeEntryGroups(groups), [groups]);

  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(entries, {
    defaultSortKey: preserveApiRowOrder ? null : "day",
    defaultSortDirection: preserveApiRowOrder ? "asc" : "desc",
  });

  const filterBar = (
    <TimeSheetFilter
      filters={filters}
      onFilterChange={onFilterChange}
      filterOptions={filterOptions}
      onSync={onRefetchTimeEntries}
      applyFilters={applyFilters}
      dateRangeDraft={dateRangeDraft}
      onDateRangeDraftChange={onDateRangeDraftChange}
      dateRangeLabel={dateRangeLabel}
      onPrevWeek={onPrevWeek}
      onNextWeek={onNextWeek}
      disableNext={disableNext}
      handleFilterReset={handleFilterReset}
      filterExpanded={filterExpanded}
      onFilterExpandedChange={onFilterExpandedChange}
      showTeam={showTeam}
      showUser={showUser}
      showTask={showTask}
      showStatus={showStatus}
      showBillingType={showBillingType}
      onLogTime={showLogTime ? tryOpenLogTime : undefined}
      onExportCsv={showExport ? onExportCsv : null}
      onExportXlsx={showExport ? onExportXlsx : null}
      showNewestFirstToggle={showNewestFirstToggle}
      newestFirst={newestFirst}
      onNewestFirstChange={onNewestFirstChange}
    />
  );

  const mainColumn =
    error && !loading ? (
      <NoDataFound message={error || "Failed to load timesheet data"} />
    ) : !loading && sortedData.length === 0 ? (
      <NoDataFound message="No timesheet data available" />
    ) : !loading ? (
      <div className="w-full">
        <TimeEntryTableTotalsBar
          dateRangeLabel={dateRangeLabel}
          loggedLabel={formatDurationWithUnit(summary.total)}
          billableLabel={formatDurationWithUnit(summary.billable)}
          className="mb-3 px-0"
        />
        <div className="relative flex flex-col min-w-0 bg-surface-primary shadow-sm transition-shadow">
          <div className="flex-1 p-2">
            <div className="overflow-x-auto">
              <table className="w-full mb-0 clients-table">
                <thead className="bg-surface-secondary">
                  <tr>
                    <SortableHeader
                      columnKey="day"
                      label="Date"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                      align="center"
                    />
                    <SortableHeader
                      columnKey="client"
                      label="Client"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                      align="center"
                    />
                    <SortableHeader
                      columnKey="project"
                      label="Project"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="task"
                      label="Task"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                      className="timesheet-task-col"
                    />
                    <SortableHeader
                      columnKey="teamMember"
                      label="Team Member"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="minutesLogged"
                      label="Logged Hours"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="billableMinutes"
                      label="Billable Hours"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    {showActionsColumn ? (
                      <SortableHeader
                        columnKey="action"
                        label="Action"
                        sortable={false}
                        className="action-col"
                      />
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((item) => (
                    <tr key={item.id}>
                      <td className="text-center align-middle whitespace-nowrap">
                        <Typography variant="span1">{item.day || "—"}</Typography>
                      </td>
                      <td className="text-center align-middle">
                        <Typography variant="span1">{item.client || "—"}</Typography>
                      </td>
                      <td>
                        <Typography variant="span1">{item.project || "—"}</Typography>
                      </td>
                      <td className="timesheet-task-col align-top">
                        {item.task ? (
                          <FloatingTooltip
                            label={item.task}
                            anchorClassName="block min-w-0 max-w-full"
                          >
                            <Typography variant="span1" className="timesheet-task-label">
                              {item.task}
                            </Typography>
                          </FloatingTooltip>
                        ) : (
                          <Typography variant="span1">—</Typography>
                        )}
                        {item.description ? (
                          <Typography
                            variant="span2"
                            className="text-text-secondary mt-1 block truncate"
                            title={item.description}
                          >
                            {item.description}
                          </Typography>
                        ) : null}
                      </td>
                      <td>
                        <Typography variant="span1">{item.teamMember || "—"}</Typography>
                      </td>
                      <td className="whitespace-nowrap">
                        <Typography variant="span1" className="font-semibold">
                          {formatDurationWithUnit(item.minutesLogged ?? item.duration)}
                        </Typography>
                      </td>
                      <td className="whitespace-nowrap">
                        <Typography variant="span1" className="font-semibold">
                          {formatDurationWithUnit(item.billableMinutes ?? item.billableDuration)}
                        </Typography>
                      </td>
                      {showActionsColumn ? (
                        <td className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {canEditTimeEntry ? (
                              <FloatingTooltip label="Edit">
                                <Button
                                  size="sm"
                                  className="btn-warning-new !w-7 !h-7 !p-0 flex items-center justify-center"
                                  aria-label="Edit time entry"
                                  onClick={() => tryEditEntry(item)}
                                >
                                  <RiPencilLine className="text-white text-[12px]" />
                                </Button>
                              </FloatingTooltip>
                            ) : null}
                            {canDeleteTimeEntry ? (
                              <FloatingTooltip label="Delete">
                                <Button
                                  size="sm"
                                  className="btn-danger-new !w-7 !h-7 !p-0 flex items-center justify-center"
                                  aria-label="Delete time entry"
                                  onClick={() => tryDeleteEntry(item)}
                                >
                                  <RiDeleteBinFill className="text-white text-[12px]" />
                                </Button>
                              </FloatingTooltip>
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>

              {pagination ? (
                <ListPaginationBar
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  pageSize={pagination.pageSize}
                  onPageChange={pagination.onPageChange}
                  onPageSizeChange={pagination.onPageSizeChange}
                  pageSizeOptions={pagination.pageSizeOptions}
                  className="px-1"
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    ) : null;

  return (
    <>
      {filterBar}

      <ContentLoader loading={loading} className="min-h-[40vh]">
        {mainColumn}
      </ContentLoader>

      <DeleteConfirmModal
        isOpen={!!entryToDelete}
        onClose={() => setEntryToDelete(null)}
        onConfirm={async () => {
          if (!entryToDelete) return;
          setDeleteLoading(true);
          try {
            if (onDeleteEntry) await onDeleteEntry(entryToDelete);
            setEntryToDelete(null);
          } finally {
            setDeleteLoading(false);
          }
        }}
        title="Delete time entry"
        itemName={entryToDelete ? `${entryToDelete.task} (${entryToDelete.duration})` : undefined}
        loading={deleteLoading}
      />

      {entryToEdit && (
        <LogTimeModal
          key={String(entryToEdit.id)}
          isOpen={!!entryToEdit}
          onClose={() => setEntryToEdit(null)}
          issue={null}
          projectId={entryToEdit.projectId}
          workItemId={entryToEdit.workItemId}
          mode="edit"
          timeLogId={entryToEdit.id}
          initialMinutesLogged={entryToEdit.minutesLogged}
          initialBillableMinutes={entryToEdit.billableMinutes}
          initialLogDate={entryToEdit.logDate}
          initialComment={entryToEdit.description}
          initialWorkItemKey={entryToEdit.workItemKey}
          initialWorkItemTitle={entryToEdit.workItemTitle}
          onSuccess={async () => {
            onRefetchTimeEntries();
          }}
        />
      )}

      {isLogTimeOpen && (
        <LogTimeModal
          isOpen={isLogTimeOpen}
          onClose={() => setIsLogTimeOpen(false)}
          issue={null}
          workItemPickerQueryParams={workItemPickerQueryParams}
          onSuccess={onRefetchTimeEntries}
        />
      )}

      <PermissionDeniedModal
        isOpen={deniedPermissionKey != null}
        onClose={() => setDeniedPermissionKey(null)}
        permissionKey={deniedPermissionKey}
      />
    </>
  );
};

export default TimeSheet;
